/* eslint-disable no-case-declarations */

/* eslint-disable no-console */

/* eslint-disable no-await-in-loop */
import { isEmpty } from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { store } from 'src/store';
import axios from 'src/workspace/axios';
import { getAppBaseUrl } from 'src/workspace/utils';
import { pendingActivitiesPayload } from './constants';
import { getCurrentUser, getRevyreWorkflowConfig } from './queries';
import {
  setAllUsers,
  setHierarchy,
  setInputFields,
  setPendingActivities,
  setPerformanceCards,
  setReferrals,
  setRevyreConfig,
  setUsers,
} from './slice';

const HIERARCHY_ENTITIES = {
  ACTOR: 'actor',
  PRODUCT: 'product',
  TERRITORY: 'territory',
};

const fetchHierarchyConfig = (entity) =>
  axios.get(`/vymo/v3/workflow/hierarchy/${entity}/config`);
const fetchUsers = (params) => axios.get('/v2/users', { params });

const fetchRevyreUsers = (params, payload) =>
  axios.post('/vymo/v3/workflow/hierarchy/actor/list', payload, { params });

const fetchTerritory = (params, payload) =>
  axios.post('/vymo/v3/workflow/hierarchy/territory/list', payload, { params });

const fetchProduct = (params, payload) =>
  axios.post('/vymo/v3/workflow/hierarchy/product/list', payload, { params });

let hierarchyWorker: Worker | null = null;
if (window.Worker) {
  hierarchyWorker = new Worker(`${getAppBaseUrl()}/worker/hierarchy.js`);
  hierarchyWorker.addEventListener('message', ({ data }) => {
    switch (data?.message) {
      case 'HIERARCHY_RESPONSE':
        const { hierarchy: revyreHierarchy } = data;
        store.dispatch(
          setHierarchy({
            revyreHierarchy,
          }),
        );
        break;
      case 'USER_HIERARCHY_RESPONSE':
        const { hierarchy: userHierarchy } = data;
        const { hierarchy, allUsers } = userHierarchy;
        store.dispatch(
          setHierarchy({
            user: hierarchy,
          }),
        );
        store.dispatch(setAllUsers(allUsers));
        break;
      default:
        break;
    }
  });
}

const fetchPaginatedBackgroundTaskForHierarchy = async (
  task,
  params,
  accumulator,
) => {
  const payload = {
    page: {
      pageNumber: 1,
      pageSize: 10000,
    },
  };
  let hasNextPage = true;
  while (hasNextPage) {
    try {
      const { data } = await task({ ...params }, payload);
      if (data?.error) {
        throw new Error();
      }
      if (!data || !data?.success) {
        hasNextPage = false;
      } else if (!isEmpty(data?.payload) && data?.payload?.result) {
        accumulator.push(...data.payload.result);
        hasNextPage = data?.payload?.currentPage < data?.payload?.maxPages;
        payload.page.pageNumber += 1;
      } else {
        hasNextPage = false;
      }
    } catch (error) {
      hasNextPage = false;
    }
  }
};

const fetchRevyreTerritory = async () => {
  try {
    const params = {
      category: 'subordinates',
    };
    const territory = [];
    const hierarchyConfig: any = await fetchHierarchyConfig(
      HIERARCHY_ENTITIES.TERRITORY,
    );
    const columnConfig = hierarchyConfig?.data?.payload?.defaultFields;
    await fetchPaginatedBackgroundTaskForHierarchy(
      fetchTerritory,
      params,
      territory,
    );
    return {
      territory,
      columnConfig,
    };
  } catch (error) {
    return [];
  }
};
const fetchRevyreProduct = async () => {
  try {
    const params = {
      category: 'subordinates',
    };
    const product = [];
    const hierarchyConfig: any = await fetchHierarchyConfig(
      HIERARCHY_ENTITIES.PRODUCT,
    );
    const columnConfig = hierarchyConfig?.data?.payload?.defaultFields;
    await fetchPaginatedBackgroundTaskForHierarchy(
      fetchProduct,
      params,
      product,
    );
    return {
      product,
      columnConfig,
    };
  } catch (error) {
    return [];
  }
};
const fetchActors = async () => {
  try {
    const params = {
      category: 'subordinates',
    };
    const hasHierarchyActorsModule = getRevyreWorkflowConfig()?.find(
      (module) =>
        module.code === HIERARCHY_ENTITIES.ACTOR && module.type === 'HIERARCHY',
    );
    if (hasHierarchyActorsModule) {
      const actors = [];
      await fetchPaginatedBackgroundTaskForHierarchy(
        fetchRevyreUsers,
        params,
        actors,
      );

      const hierarchyConfig: any = await fetchHierarchyConfig(
        HIERARCHY_ENTITIES.ACTOR,
      );
      const columnConfig = hierarchyConfig?.data?.payload?.defaultFields;

      const rootUser = getCurrentUser();

      hierarchyWorker?.postMessage(
        JSON.stringify({
          message: 'CONSTRUCT_USER_HIERARCHY',
          rootUser,
          users: actors,
          managers: [],
        }),
      );
      return { subordinates: actors, managers: [], columnConfig };
    }
    return {};
  } catch (error) {
    return {};
  }
};
const fetchPaginatedBackgroundTask = async (task, params, accumulator) => {
  let hasNextPage = true;
  let timestamp;
  let page = 0;
  while (hasNextPage) {
    try {
      const { data } = await task(params);
      if (data.error) {
        throw new Error();
      }
      if (!isEmpty(data.results)) {
        accumulator.push(...data.results);
        params.skip += data.results.length;
        hasNextPage = data.options && data.options.has_next_page;
        if (page === 0) {
          timestamp = data.last_updated;
        }
        page += 1;
      } else if ('results' in data) {
        hasNextPage = false;
      }
    } catch (error) {
      hasNextPage = false;
    }
  }
  if (timestamp) {
    params.timestamp = timestamp;
  }
};
const fetchSubordinates = async () => {
  try {
    const params = {
      skip: 0,
      category: 'subordinates',
    };
    const subordinates = [];
    await fetchPaginatedBackgroundTask(fetchUsers, params, subordinates);

    return subordinates;
  } catch (error) {
    return [];
  }
};
const fetchManagers = async () => {
  try {
    const params = {
      skip: 0,
      category: 'managers',
    };
    const managers = [];
    await fetchPaginatedBackgroundTask(fetchUsers, params, managers);
    return managers;
  } catch (error) {
    return [];
  }
};
const fetchSubordinatesWithManagers = async () => {
  try {
    const results: any = await Promise.allSettled([
      fetchSubordinates(),
      fetchManagers(),
    ]);
    const subordinatesWithManagers = {
      subordinates: results[0]?.value || [],
      managers: results[1]?.value || [],
    };

    const rootUser = getCurrentUser();
    hierarchyWorker?.postMessage(
      JSON.stringify({
        message: 'CONSTRUCT_USER_HIERARCHY',
        rootUser,
        users: subordinatesWithManagers.subordinates,
        managers: subordinatesWithManagers.managers,
      }),
    );
    return subordinatesWithManagers;
  } catch (error) {
    return { subordinates: [], managers: [] };
  }
};
const fetchPeers = async () => {
  try {
    const params = {
      skip: 0,
      category: 'peers',
    };
    const peers = [];
    await fetchPaginatedBackgroundTask(fetchUsers, params, peers);
    return { peers };
  } catch (error) {
    return { peers: [] };
  }
};
const fetchDirectReports = async () => {
  try {
    const params = {
      skip: 0,
      category: 'direct_reports',
    };
    const directReports = [];
    await fetchPaginatedBackgroundTask(fetchUsers, params, directReports);
    return { directReports };
  } catch (error) {
    return { directReports: [] };
  }
};
const fetchSuperManagers = async () => {
  try {
    const params = {
      skip: 0,
      category: 'super_managers',
    };
    const superManagers = [];
    await fetchPaginatedBackgroundTask(fetchUsers, params, superManagers);
    return { superManagers };
  } catch (error) {
    return { superManagers: [] };
  }
};

export const initUsers = createAsyncThunk(
  'lmsInit/users',
  async (_, { dispatch }) => {
    try {
      let users = {};
      const revyreModules = getRevyreWorkflowConfig();
      const hasHierarchyActorsModule = revyreModules?.find(
        (module) => module.code === 'actor' && module.type === 'HIERARCHY',
      );
      if (hasHierarchyActorsModule) {
        const {
          subordinates = [],
          managers = [],
          columnConfig = [],
        } = await fetchActors();
        users = {
          subordinates,
          managers,
          columnConfig,
        };
      } else {
        const results = await Promise.allSettled([
          fetchSubordinatesWithManagers(),
          fetchPeers(),
          fetchDirectReports(),
          fetchSuperManagers(),
        ]);

        results.forEach((res: any, idx) => {
          const taskName = [
            'subordinates',
            'peers',
            'directReports',
            'superManagers',
          ][idx];
          if (res.status === 'fulfilled') {
            users = {
              ...users,
              ...(res.value || {}),
            };
          } else {
            console.error(
              'Fetch failed. Reason:',
              taskName,
              res.reason?.message,
            );
          }
        });
      }
      dispatch(setUsers(users));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fetching users failed', error);
    }
  },
);

export const initRevyreHierarchy = createAsyncThunk(
  'lmsInit/revyreHierarchy',
  async () => {
    const revyreModules = getRevyreWorkflowConfig();
    const hasTerritoryModule = revyreModules?.find(
      (module) =>
        module.code === HIERARCHY_ENTITIES.TERRITORY &&
        module.type === 'HIERARCHY',
    );
    const hasProductModule = revyreModules?.find(
      (module) =>
        module.code === HIERARCHY_ENTITIES.PRODUCT &&
        module.type === 'HIERARCHY',
    );
    if (!hasTerritoryModule && !hasProductModule) {
      return;
    }
    let territory: any = null;
    let product: any = null;
    try {
      if (hasTerritoryModule) {
        territory = await fetchRevyreTerritory();
      }
      if (hasProductModule) {
        product = await fetchRevyreProduct();
      }
      hierarchyWorker?.postMessage(
        JSON.stringify({
          message: 'CONSTRUCT_HIERARCHY',
          territory,
          product,
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fetching revyre hierarchy failed', error);
    }
  },
);

export const initReferrals = createAsyncThunk(
  'lmsInit/referrals',
  async (_, { dispatch }) => {
    try {
      const referrals: any = [];
      const params = {
        skip: 0,
        timestamp: 0,
      };
      let hasNextPage = true;
      while (hasNextPage) {
        const res: any = await axios.get('/leads/referrals', { params });
        if (res.error) {
          throw new Error();
        }
        const { data } = res;
        if (!isEmpty(data?.results)) {
          referrals.push(...(data?.results || []));
          params.skip += data.results.length;
          hasNextPage = data?.options && data.options.has_next_page;
        } else {
          hasNextPage = false;
        }
      }
      dispatch(setReferrals(referrals));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Fetching referrals failed', error);
    }
  },
);

export const initRevyreConfig = createAsyncThunk(
  'lmsInit/revyreConfig',
  async (_, { dispatch }) => {
    const revyreModules = getRevyreWorkflowConfig()?.filter(
      (module) => module.type !== 'HIERARCHY',
    );
    const revyreModuleConfig = {};
    await Promise.all(
      revyreModules.map(async (module) => {
        const { code } = module;
        const res = await axios.get(`/vymo/v3/workflow/${code}/config`);
        revyreModuleConfig[code] = res.data.payload?.flows?.[0] || {};
      }),
    );
    console.log('Revyre Module Configs:', revyreModuleConfig);
    dispatch(setRevyreConfig(revyreModuleConfig));
  },
);

export const getInputFields = createAsyncThunk(
  'lmsConfig/getInputFields',
  async (_, { dispatch }) => {
    try {
      const response = await axios.get('/input_fields');
      dispatch(setInputFields(response.data));
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);
    }
  },
);

export const fetchPendingActivities = createAsyncThunk(
  'activities/getActivities',
  async (_, { dispatch }) => {
    try {
      const response = await axios.post(
        '/vymo/v3/workflow/activity/list',
        pendingActivitiesPayload,
      );
      dispatch(setPendingActivities(response.data?.payload?.result));
    } catch (error) {
      console.error(error);
    }
  },
);

export const initPerformanceCards = createAsyncThunk(
  'performance/cards',
  async (_, { dispatch }) => {
    try {
      const { data } = await axios.get('/performance/v1/cards');
      dispatch(
        setPerformanceCards(
          (data?.results || []).filter(
            (card) => card.type === 'performance_card',
          ),
        ),
      );
    } catch (error) {
      console.error(error);
    }
  },
);
